import { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query, where, writeBatch, getDocs,
} from "firebase/firestore";
import { formatPence, whatsAppLink } from "./lib/pricing";
import PlayerSearchInput from "./PlayerSearchInput";
import AdminResults from "./AdminResults";

var GRADES = ["Grade A", "Grade B", "Grade C"];

var card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.15)", borderRadius: 14, padding: "20px 22px" };
var inputStyle = { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "sans-serif" };
var labelStyle = { fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 };
var btnStyle = { padding: "11px 18px", background: "linear-gradient(135deg,#FFB020,#FF6B00)", border: "none", borderRadius: 8, color: "#000", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" };
var btnSecondaryStyle = { padding: "8px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#ccc", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "sans-serif" };

function emptyForm() {
  return {
    name: "", date: "", venue: "", earlyBirdCutoff: "",
    grades: { "Grade A": true, "Grade B": false, "Grade C": false },
    pricing: {
      "Grade A": { earlyBird: "", late: "" },
      "Grade B": { earlyBird: "", late: "" },
      "Grade C": { earlyBird: "", late: "" },
    },
    maxEntries: { "Grade A": "", "Grade B": "", "Grade C": "" },
  };
}

function toPence(v) {
  var n = parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function fromPence(v) {
  return v === undefined || v === null ? "" : (v / 100).toFixed(2);
}

function TournamentForm(props) {
  var editing = props.editing;
  var onDone = props.onDone;
  var [form, setForm] = useState(function() {
    if (!editing) return emptyForm();
    var base = emptyForm();
    base.name = editing.name || "";
    base.date = editing.date || "";
    base.venue = editing.venue || "";
    base.earlyBirdCutoff = editing.earlyBirdCutoff || "";
    GRADES.forEach(function(g) {
      base.grades[g] = editing.grades ? editing.grades.indexOf(g) !== -1 : false;
      if (editing.pricing && editing.pricing[g]) {
        base.pricing[g] = { earlyBird: fromPence(editing.pricing[g].earlyBird), late: fromPence(editing.pricing[g].late) };
      }
      if (editing.maxEntries && editing.maxEntries[g]) {
        base.maxEntries[g] = String(editing.maxEntries[g]);
      }
    });
    return base;
  });
  var [error, setError] = useState("");
  var [saving, setSaving] = useState(false);

  function setField(key, val) { setForm(function(f) { return { ...f, [key]: val }; }); }
  function toggleGrade(g) { setForm(function(f) { return { ...f, grades: { ...f.grades, [g]: !f.grades[g] } }; }); }
  function setPrice(g, tier, val) {
    setForm(function(f) { return { ...f, pricing: { ...f.pricing, [g]: { ...f.pricing[g], [tier]: val } } }; });
  }
  function setMaxEntries(g, val) {
    setForm(function(f) { return { ...f, maxEntries: { ...f.maxEntries, [g]: val } }; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    var selectedGrades = GRADES.filter(function(g) { return form.grades[g]; });
    if (!form.name.trim()) { setError("Tournament name is required."); return; }
    if (!form.date) { setError("Date is required."); return; }
    if (selectedGrades.length === 0) { setError("Select at least one grade."); return; }
    if (!form.earlyBirdCutoff) { setError("Early bird cutoff date is required."); return; }
    for (var i = 0; i < selectedGrades.length; i++) {
      var g = selectedGrades[i];
      if (!form.pricing[g].earlyBird || !form.pricing[g].late) {
        setError("Enter both early bird and late prices for " + g + ".");
        return;
      }
    }

    var pricing = {};
    var maxEntries = {};
    selectedGrades.forEach(function(g) {
      pricing[g] = { earlyBird: toPence(form.pricing[g].earlyBird), late: toPence(form.pricing[g].late) };
      var maxRaw = parseInt(form.maxEntries[g], 10);
      maxEntries[g] = Number.isInteger(maxRaw) && maxRaw > 0 ? maxRaw : null;
    });

    var data = {
      name: form.name.trim(),
      date: form.date,
      venue: form.venue.trim(),
      grades: selectedGrades,
      pricing: pricing,
      maxEntries: maxEntries,
      earlyBirdCutoff: form.earlyBirdCutoff,
      status: editing ? editing.status : "open",
    };

    setSaving(true);
    var ref = editing ? doc(db, "tournaments", editing.id) : doc(collection(db, "tournaments"));
    var write = editing ? updateDoc(ref, data) : setDoc(ref, { ...data, createdAt: new Date().toISOString() });
    write
      .then(function() { onDone(); })
      .catch(function(err) { setError(err.message); })
      .finally(function() { setSaving(false); });
  }

  return (
    <form onSubmit={handleSubmit} style={{ ...card, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0, fontSize: 16, color: "#FFB020" }}>{editing ? "Edit Tournament" : "New Tournament"}</h3>
      {error && <div style={{ background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#ff8080", marginBottom: 14 }}>{error}</div>}

      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Name</div>
        <input style={inputStyle} value={form.name} onChange={function(e) { setField("name", e.target.value); }} placeholder="EDC Autumn Open" />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Date</div>
          <input style={inputStyle} type="date" value={form.date} onChange={function(e) { setField("date", e.target.value); }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>Venue</div>
          <input style={inputStyle} value={form.venue} onChange={function(e) { setField("venue", e.target.value); }} placeholder="Optional" />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Early Bird Cutoff (price goes up after this date)</div>
        <input style={inputStyle} type="date" value={form.earlyBirdCutoff} onChange={function(e) { setField("earlyBirdCutoff", e.target.value); }} />
      </div>

      <div style={{ marginBottom: 6 }}>
        <div style={labelStyle}>Grades &amp; Pricing</div>
      </div>
      {GRADES.map(function(g) {
        var on = form.grades[g];
        return (
          <div key={g} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: on ? "#fff" : "#888", cursor: "pointer" }}>
              <input type="checkbox" checked={on} onChange={function() { toggleGrade(g); }} />
              {g}
            </label>
            {on && (
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>Early Bird £</div>
                  <input style={inputStyle} type="number" min="0" step="0.01" value={form.pricing[g].earlyBird} onChange={function(e) { setPrice(g, "earlyBird", e.target.value); }} placeholder="15.00" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>Late £</div>
                  <input style={inputStyle} type="number" min="0" step="0.01" value={form.pricing[g].late} onChange={function(e) { setPrice(g, "late", e.target.value); }} placeholder="20.00" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>Max Entries</div>
                  <input style={inputStyle} type="number" min="1" step="1" value={form.maxEntries[g]} onChange={function(e) { setMaxEntries(g, e.target.value); }} placeholder="Unlimited" />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button type="submit" style={btnStyle} disabled={saving}>{saving ? "Saving..." : editing ? "Save Changes" : "Create Tournament"}</button>
        {editing && <button type="button" style={btnSecondaryStyle} onClick={onDone}>Cancel</button>}
      </div>
    </form>
  );
}

function UserRow(props) {
  var user = props.user;
  var [editing, setEditing] = useState(false);
  var [pending, setPending] = useState(user.playerName || "");
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState("");

  function startEdit() { setPending(user.playerName || ""); setEditing(true); setError(""); }

  // Also fixes up any tournament entries this player already made — their
  // playerName is a snapshot taken at registration time (it's what powers the
  // public entrant list and average lookup), so correcting the link here
  // wouldn't otherwise be reflected on entries created before the fix.
  function save() {
    setSaving(true);
    setError("");
    var newName = pending.trim() ? pending.trim() : null;
    updateDoc(doc(db, "users", user.id), { playerName: newName })
      .then(function() {
        if (!newName) return;
        return getDocs(query(collection(db, "tournamentEntries"), where("uid", "==", user.id)));
      })
      .then(function(snap) {
        if (!snap || snap.empty) return;
        var batch = writeBatch(db);
        snap.docs.forEach(function(d) { batch.update(d.ref, { playerName: newName }); });
        return batch.commit();
      })
      .then(function() { setEditing(false); })
      .catch(function(err) { setError(err.message); })
      .finally(function() { setSaving(false); });
  }

  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, color: "#ccc" }}>{user.email}</div>
          <div style={{ fontSize: 13, marginTop: 2, color: user.playerName ? "#ddd" : "#FFB020", fontWeight: user.playerName ? 400 : 600 }}>
            {user.playerName || "Not linked"}
          </div>
        </div>
        {!editing && <button style={btnSecondaryStyle} onClick={startEdit}>Change link</button>}
      </div>
      {editing && (
        <div style={{ marginTop: 10 }}>
          {error && <div style={{ color: "#ff8080", fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <PlayerSearchInput value={pending} onSelect={setPending} placeholder="Search or type a name..." />
          </div>
          <button style={btnStyle} onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
          <button style={btnSecondaryStyle} onClick={function() { setPending(""); }}>Clear link</button>
          <button style={btnSecondaryStyle} onClick={function() { setEditing(false); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersPanel() {
  var [users, setUsers] = useState([]);
  var [search, setSearch] = useState("");

  useEffect(function() {
    var unsub = onSnapshot(collection(db, "users"), function(snap) {
      var list = snap.docs.map(function(d) { return { id: d.id, ...d.data() }; });
      list.sort(function(a, b) { return (a.email || "").localeCompare(b.email || ""); });
      setUsers(list);
    });
    return unsub;
  }, []);

  var q = search.trim().toLowerCase();
  var shown = !q ? users : users.filter(function(u) {
    return (u.email || "").toLowerCase().indexOf(q) !== -1 || (u.playerName || "").toLowerCase().indexOf(q) !== -1;
  });

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0, fontSize: 16, color: "#FFB020" }}>Users</h3>
      <p style={{ fontSize: 12, color: "#666", marginTop: 0 }}>Fix a missed or incorrect result link — search by email or player name.</p>
      <input style={{ ...inputStyle, marginBottom: 10 }} value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Search users..." />
      {shown.length === 0 && <p style={{ fontSize: 13, color: "#666" }}>No users found.</p>}
      {shown.map(function(u) { return <UserRow key={u.id} user={u} />; })}
    </div>
  );
}

function TournamentList(props) {
  var tournaments = props.tournaments;
  var onEdit = props.onEdit;
  var onSelectEntries = props.onSelectEntries;
  var selectedId = props.selectedId;

  function toggleStatus(t) {
    updateDoc(doc(db, "tournaments", t.id), { status: t.status === "open" ? "closed" : "open" });
  }

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0, fontSize: 16, color: "#FFB020" }}>Tournaments</h3>
      {tournaments.length === 0 && <p style={{ fontSize: 13, color: "#666" }}>No tournaments yet — create one above.</p>}
      {tournaments.map(function(t) {
        return (
          <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ cursor: "pointer" }} onClick={function() { onSelectEntries(t.id); }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: selectedId === t.id ? "#FFB020" : "#ddd" }}>{t.name}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {new Date(t.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                {" · " + t.status}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btnSecondaryStyle} onClick={function() { onEdit(t); }}>Edit</button>
              <button style={btnSecondaryStyle} onClick={function() { toggleStatus(t); }}>{t.status === "open" ? "Close entry" : "Reopen"}</button>
              <button style={{ ...btnSecondaryStyle, background: selectedId === t.id ? "rgba(255,176,32,0.15)" : btnSecondaryStyle.background, color: selectedId === t.id ? "#FFB020" : btnSecondaryStyle.color }} onClick={function() { onSelectEntries(t.id); }}>Entries</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ManualEntryForm(props) {
  var tournament = props.tournament;
  var onDone = props.onDone;
  var [grade, setGrade] = useState(tournament.grades[0] || "");
  var [playerName, setPlayerName] = useState("");
  var [phone, setPhone] = useState("");
  var [paid, setPaid] = useState(true);
  var [error, setError] = useState("");
  var [saving, setSaving] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!grade) { setError("Select a grade."); return; }
    if (!playerName.trim()) { setError("Enter a player name."); return; }
    setSaving(true);
    var entryRef = doc(collection(db, "tournamentEntries"));
    var privateRef = doc(db, "tournamentEntryPrivate", entryRef.id);
    var batch = writeBatch(db);
    batch.set(entryRef, {
      uid: null,
      tournamentId: tournament.id,
      playerName: playerName.trim(),
      grade: grade,
      paymentStatus: paid ? "paid" : "pending",
      manual: true,
      createdAt: new Date().toISOString(),
    });
    batch.set(privateRef, {
      uid: null,
      tournamentId: tournament.id,
      phone: phone.trim(),
      whatsappConfirmed: false,
      createdAt: new Date().toISOString(),
    });
    batch.commit()
      .then(function() { onDone(); })
      .catch(function(err) { setError(err.message); })
      .finally(function() { setSaving(false); });
  }

  return (
    <form onSubmit={handleSubmit} style={{ border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#FFB020", marginBottom: 10 }}>Add Manual Entry</div>
      <p style={{ fontSize: 11, color: "#666", marginTop: 0, marginBottom: 10 }}>For entries taken outside the app — cash, bank transfer, or in person.</p>
      {error && <div style={{ background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#ff8080", marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={labelStyle}>Grade</div>
          <select style={inputStyle} value={grade} onChange={function(e) { setGrade(e.target.value); }}>
            {tournament.grades.map(function(g) { return <option key={g} value={g}>{g}</option>; })}
          </select>
        </div>
        <div style={{ flex: 2, minWidth: 180 }}>
          <div style={labelStyle}>Player Name</div>
          <PlayerSearchInput value={playerName} onSelect={setPlayerName} placeholder="Search or type a name..." />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={labelStyle}>Phone (optional)</div>
          <input style={inputStyle} type="tel" value={phone} onChange={function(e) { setPhone(e.target.value); }} placeholder="07700 900123" />
        </div>
        <div style={{ display: "flex", gap: 14, paddingBottom: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: paid ? "#6DC878" : "#888", cursor: "pointer" }}>
            <input type="radio" checked={paid} onChange={function() { setPaid(true); }} /> Paid
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: !paid ? "#FFB020" : "#888", cursor: "pointer" }}>
            <input type="radio" checked={!paid} onChange={function() { setPaid(false); }} /> Pending
          </label>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" style={btnStyle} disabled={saving}>{saving ? "Adding..." : "Add Entry"}</button>
        <button type="button" style={btnSecondaryStyle} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function EntryRow(props) {
  var e = props.entry;
  var priv = props.priv;
  var link = priv ? whatsAppLink(priv.phone) : null;
  var [editingName, setEditingName] = useState(false);
  var [pendingName, setPendingName] = useState(e.playerName);
  var [savingName, setSavingName] = useState(false);
  var [nameError, setNameError] = useState("");

  function toggleWhatsapp() {
    updateDoc(doc(db, "tournamentEntryPrivate", e.id), { whatsappConfirmed: !priv.whatsappConfirmed });
  }

  function togglePaid() {
    updateDoc(doc(db, "tournamentEntries", e.id), { paymentStatus: e.paymentStatus === "paid" ? "pending" : "paid" });
  }

  function removeEntry() {
    if (!window.confirm("Remove this entry entirely? This can't be undone.")) return;
    deleteDoc(doc(db, "tournamentEntries", e.id));
    deleteDoc(doc(db, "tournamentEntryPrivate", e.id));
  }

  // Fixes this specific entry's displayed name directly — the reliable path
  // when the uid-based sync from the Users panel can't find it (a manual
  // entry with no uid, or the account it's actually tied to being a
  // different one than whichever profile got corrected there).
  function saveName() {
    if (!pendingName.trim()) { setNameError("Name can't be empty."); return; }
    setSavingName(true);
    setNameError("");
    updateDoc(doc(db, "tournamentEntries", e.id), { playerName: pendingName.trim() })
      .then(function() { setEditingName(false); })
      .catch(function(err) { setNameError(err.message); })
      .finally(function() { setSavingName(false); });
  }

  return (
    <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          {!editingName ? (
            <div style={{ fontSize: 14, fontWeight: 700, color: "#ddd", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {e.playerName}
              {e.manual && <span style={{ fontSize: 10, fontWeight: 700, color: "#7BA7FF", border: "1px solid rgba(123,167,255,0.4)", borderRadius: 20, padding: "1px 7px" }}>MANUAL</span>}
              {!e.uid && !e.manual && <span style={{ fontSize: 10, fontWeight: 700, color: "#FFB020", border: "1px solid rgba(255,176,32,0.4)", borderRadius: 20, padding: "1px 7px" }}>NO ACCOUNT LINK</span>}
              <button style={{ ...btnSecondaryStyle, padding: "2px 8px", fontSize: 11 }} onClick={function() { setPendingName(e.playerName); setNameError(""); setEditingName(true); }}>Edit</button>
            </div>
          ) : (
            <div>
              <PlayerSearchInput value={pendingName} onSelect={setPendingName} placeholder="Search or type a name..." />
              {nameError && <div style={{ color: "#ff8080", fontSize: 11, marginTop: 4 }}>{nameError}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button style={btnSecondaryStyle} onClick={saveName} disabled={savingName}>{savingName ? "Saving..." : "Save name"}</button>
                <button style={btnSecondaryStyle} onClick={function() { setEditingName(false); }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
            {e.grade}
            <span style={{ marginLeft: 8, color: e.paymentStatus === "paid" ? "#6DC878" : "#FFB020" }}>{e.paymentStatus === "paid" ? "Paid" : "Pending payment"}</span>
            {e.amountPaid != null && <span style={{ marginLeft: 8, color: "#555" }}>{formatPence(e.amountPaid)}</span>}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{!priv ? "Loading..." : priv.phone ? priv.phone : "No phone on file"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {link && <a href={link} target="_blank" rel="noreferrer" style={{ ...btnSecondaryStyle, textDecoration: "none", display: "inline-block" }}>WhatsApp</a>}
          <button style={btnSecondaryStyle} onClick={togglePaid}>{e.paymentStatus === "paid" ? "Mark unpaid" : "Mark paid"}</button>
          <button style={{ ...btnSecondaryStyle, color: "#ff8080", borderColor: "rgba(255,80,80,0.3)" }} onClick={removeEntry}>Remove</button>
        </div>
      </div>
      {priv && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: priv.whatsappConfirmed ? "#6DC878" : "#888", marginTop: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={!!priv.whatsappConfirmed} onChange={toggleWhatsapp} />
          In WhatsApp group
        </label>
      )}
    </div>
  );
}

function EntriesPanel(props) {
  var tournament = props.tournament;
  var [entries, setEntries] = useState([]);
  var [privates, setPrivates] = useState({});
  var [showManualForm, setShowManualForm] = useState(false);

  useEffect(function() {
    var unsub1 = onSnapshot(query(collection(db, "tournamentEntries"), where("tournamentId", "==", tournament.id)), function(snap) {
      setEntries(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
    });
    var unsub2 = onSnapshot(query(collection(db, "tournamentEntryPrivate"), where("tournamentId", "==", tournament.id)), function(snap) {
      var map = {};
      snap.docs.forEach(function(d) { map[d.id] = d.data(); });
      setPrivates(map);
    });
    return function() { unsub1(); unsub2(); };
  }, [tournament.id]);

  var sorted = entries.slice().sort(function(a, b) { return a.grade.localeCompare(b.grade) || a.playerName.localeCompare(b.playerName); });

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showManualForm ? 14 : 0 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: "#FFB020" }}>{"Entries · " + tournament.name}</h3>
        {!showManualForm && <button style={btnSecondaryStyle} onClick={function() { setShowManualForm(true); }}>+ Add Manual Entry</button>}
      </div>
      {showManualForm && <ManualEntryForm tournament={tournament} onDone={function() { setShowManualForm(false); }} />}
      {sorted.length === 0 && <p style={{ fontSize: 13, color: "#666" }}>No entries yet.</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sorted.map(function(e) { return <EntryRow key={e.id} entry={e} priv={privates[e.id]} />; })}
      </div>
    </div>
  );
}

export default function AdminPage() {
  var [tournaments, setTournaments] = useState([]);
  var [editing, setEditing] = useState(null);
  var [selectedEntriesId, setSelectedEntriesId] = useState(null);

  useEffect(function() {
    var unsub = onSnapshot(collection(db, "tournaments"), function(snap) {
      var list = snap.docs.map(function(d) { return { id: d.id, ...d.data() }; });
      list.sort(function(a, b) { return b.date.localeCompare(a.date); });
      setTournaments(list);
    });
    return unsub;
  }, []);

  var selectedTournament = tournaments.find(function(t) { return t.id === selectedEntriesId; });

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 20px", color: "#fff", fontFamily: "sans-serif" }}>
      <h2 style={{ fontSize: 18, marginBottom: 20, textTransform: "uppercase", letterSpacing: 1, color: "#FFB020" }}>Admin</h2>

      <div style={{ marginBottom: 20 }}>
        <AdminResults entryTournaments={tournaments} />
      </div>

      <TournamentForm key={editing ? editing.id : "new"} editing={editing} onDone={function() { setEditing(null); }} />

      <TournamentList tournaments={tournaments} onEdit={setEditing} onSelectEntries={setSelectedEntriesId} selectedId={selectedEntriesId} />

      {selectedTournament && <EntriesPanel tournament={selectedTournament} />}

      <div style={{ marginTop: 20 }}>
        <UsersPanel />
      </div>
    </div>
  );
}
